// ---------------------------------------------------------------------------
// Traductions du corpus philosophique (90 entrées seed).
// Indexé par position stable dans le corpus (0-89), tel que retourné par
// GetTodayInspiration.corpusIndex.
//
// Les entrées sont regroupées par catégorie (9 par catégorie, 10 catégories) :
//   0-8   action
//   9-17  perception
//  18-26  acceptation
//  27-35  discipline
//  36-44  relations
//  45-53  recuperation
//  54-62  travail
//  63-71  crainte
//  72-80  temps
//  81-89  clarte
// ---------------------------------------------------------------------------

type EntryI18n = readonly [text: string, explanation: string, challenge: string];

const PHILOSOPHY_EN: readonly EntryI18n[] = [
  // ── action (0-8) ──
  ['What depends on you deserves your effort; what does not deserves your peace.',
   'Separating the two avoids spending energy on what you cannot change.',
   'Name one thing in your day that truly depends on you.'],
  ['Acting rightly counts more than succeeding quickly.',
   'The rightness of an action remains true even when its result is slow to appear.',
   'Choose one action today for its rightness rather than its speed.'],
  ['An action begun is worth more than a perfect project left motionless.',
   'Beginning, even imperfectly, already produces information that planning alone does not give.',
   'Start the simplest version of something you have been putting off.'],
  ['The control you have over your attitude remains whole, even when everything else escapes.',
   'Attitude is one of the few domains where control never depends on circumstances.',
   'Consciously choose your attitude for the next hour.'],
  ['Doing what is in your power is enough; the rest does not belong to you.',
   'Demanding of yourself what exceeds your real power produces fatigue without benefit.',
   'Identify a demand you place on an outcome that does not depend entirely on you.'],
  ['Every gesture made today builds tomorrow\'s capacity.',
   'A capacity is built through repetition, rarely through a single isolated gesture.',
   'Spot one modest gesture today that serves a larger goal.'],
  ['Intention alone changes nothing; a gesture, even a modest one, changes something.',
   'An intention not translated into action remains without effect on reality.',
   'Turn a current intention into a concrete gesture, however small.'],
  ['Choose the action that remains right, regardless of its outcome.',
   'The outcome depends on multiple factors; the rightness of the action remains under your control.',
   'Reconsider a recent decision solely by the standard of its rightness, not its outcome.'],
  ['What you do with constancy weighs more than what you do with intensity.',
   'A one-off effort, however intense, rarely produces what modest constancy does.',
   'Choose an action you can repeat tomorrow, even at reduced intensity.'],

  // ── perception (9-17) ──
  ['Describing a situation without exaggerating is already an act of clarity.',
   'Removing the dramatic layer from a description lets the real facts emerge.',
   'Describe a current difficulty using only observable facts, without adjectives.'],
  ['Your judgment of an event is not the event itself.',
   'What you add to a fact through interpretation is often heavier than the fact itself.',
   'Identify a judgment you have added to a neutral event today.'],
  ['What you see depends on where you stand. Move before concluding.',
   'Changing perspective often reveals dimensions invisible from the original position.',
   'Look at a current situation from someone else\'s perspective for a moment.'],
  ['The same fact can be a catastrophe or a signal, depending on the story you tell about it.',
   'The narrative wrapped around a fact determines the emotional response more than the fact itself.',
   'Take an event that bothered you and tell it differently, as a neutral observer would.'],
  ['Before interpreting, observe. Before judging, describe.',
   'Rushing to interpretation often closes the door to what observation could still reveal.',
   'Spend two minutes observing something without labeling it good or bad.'],
  ['What annoys you in others often reflects something you refuse to see in yourself.',
   'The irritation triggered by another person sometimes points to an unrecognized part of oneself.',
   'Notice one thing that irritated you today and ask what it might say about you.'],
  ['A narrowed perspective shrinks the field of possibilities.',
   'When a single interpretation dominates, other plausible readings become invisible.',
   'Identify a situation where you have only one interpretation and imagine two others.'],
  ['You do not see the world as it is; you see it as you are.',
   'Every perception is filtered through your current state, not through a neutral lens.',
   'Notice how your current mood colors what you perceive right now.'],
  ['The quality of your attention shapes the quality of your judgment.',
   'A scattered attention produces approximate judgments; a focused attention refines them.',
   'Give your full attention to a single thing for the next five minutes.'],

  // ── acceptation (18-26) ──
  ['Accepting does not mean approving. It means stopping the fight against what already is.',
   'The energy spent fighting an established fact is energy unavailable for what can still change.',
   'Identify something you are fighting against that is already past or unchangeable.'],
  ['What resists you is not necessarily your enemy.',
   'An obstacle sometimes protects you from a path that was not right, or teaches you what the right path requires.',
   'Find one thing that resisted you this week and consider what it may have taught you.'],
  ['The present moment is never unbearable when you stop adding yesterday and tomorrow to it.',
   'What makes the present heavy is often the accumulation of past regret and future anxiety.',
   'Focus for one minute on what is happening right now, without adding what came before or what comes next.'],
  ['You cannot change what has already happened, only what you do with it.',
   'The past is fixed; your relationship to it remains open.',
   'Choose a past event and decide what you will do with it today, rather than what it did to you.'],
  ['Resistance to what is already true consumes energy that action could use.',
   'Accepting a reality frees up the energy that denial was using to keep it at bay.',
   'Name a reality you have been resisting and feel what changes when you simply name it.'],
  ['What you accept stops occupying your mind; what you resist stays.',
   'Mental space occupied by resistance is unavailable for creation or decision.',
   'Identify one thought that keeps returning and accept its presence without fighting it.'],
  ['Some things are not yours to carry.',
   'Distinguishing between what belongs to you and what belongs to the world lightens the load.',
   'Put down one worry that is not truly yours to carry.'],
  ['Accepting your limits is not weakness; it is the condition for exceeding them.',
   'Knowing where your limits lie lets you work with them rather than against them.',
   'Name a limit you have been denying and consider how acknowledging it could serve you.'],
  ['What is, is. Your power lies in what you do next.',
   'No amount of protest changes an established fact; only the next action matters.',
   'Face a fact you have been avoiding and ask only: what do I do now?'],

  // ── discipline (27-35) ──
  ['Discipline is not punishment. It is the structure that protects what matters to you.',
   'A framework freely chosen makes freedom possible; without it, every day starts from scratch.',
   'Identify a small discipline that already protects something important to you.'],
  ['The difficulty of a discipline decreases with its regularity.',
   'What costs enormous effort the first time becomes almost automatic after enough repetitions.',
   'Choose a discipline you want to strengthen and commit to a minimal version of it every day this week.'],
  ['What you do every day counts more than what you do once in a while.',
   'The accumulation of small daily actions outweighs the occasional grand gesture.',
   'Find one small thing you do every day that, cumulatively, has already changed something.'],
  ['Discipline is choosing what you want most over what you want now.',
   'Every disciplined choice is a vote for a longer-term desire against an immediate impulse.',
   'Identify a choice today where you can favor what you want deeply over what you want right now.'],
  ['A discipline imposed by fear does not last. A discipline chosen for meaning endures.',
   'External pressure produces compliance; internal meaning produces commitment.',
   'Examine one of your disciplines: does it come from fear or from meaning?'],
  ['The form of a discipline can change; its presence in your life cannot.',
   'A disrupted routine does not mean a lost discipline — only one that needs a new shape.',
   'Find a discipline that has been disrupted and give it a form that fits your current life.'],
  ['Discipline is not rigidity. It is the backbone that lets the rest stay flexible.',
   'A solid framework allows improvisation without collapse.',
   'Notice where a solid structure in your life allows you to be flexible elsewhere.'],
  ['Missed once is a moment. Missed twice is the beginning of a new pattern.',
   'A single slip is noise; two in a row is a signal worth paying attention to.',
   'Look at a recent slip and decide whether it was a moment or the start of a pattern.'],
  ['True freedom is not the absence of constraints but the mastery of which ones you accept.',
   'Choosing your constraints deliberately is the opposite of being imprisoned by them.',
   'Name one constraint you have freely chosen that actually increases your freedom.'],

  // ── relations (36-44) ──
  ['The quality of your listening determines the quality of your presence.',
   'Listening is not waiting for your turn to speak; it is making space for the other person to exist.',
   'In your next conversation, listen without preparing your response.'],
  ['You cannot control what others think of you, only the integrity with which you act.',
   'The energy spent managing your image is energy stolen from being who you are.',
   'Identify a moment today when you acted to manage an impression rather than from integrity.'],
  ['A sincere word, even clumsy, carries more weight than a polished silence.',
   'Saying something imperfect but true strengthens a bond more than saying nothing perfectly.',
   'Say something true to someone today, even if the words are not polished.'],
  ['Expecting others to read your mind is giving them a task they will always fail.',
   'Unspoken expectations are invisible obligations that the other person never agreed to.',
   'Name an expectation you hold toward someone that you have never clearly expressed.'],
  ['Conflict well handled brings people closer. Conflict avoided pushes them apart.',
   'Avoiding a difficult conversation does not preserve peace; it preserves distance.',
   'Identify a conversation you have been avoiding and consider what it might open if held well.'],
  ['You are not responsible for the emotions of others, only for your own conduct.',
   'Trying to manage the emotional state of another adult is both impossible and exhausting.',
   'Notice where you are carrying responsibility for someone else\'s emotional state.'],
  ['Gratitude expressed strengthens the bond. Gratitude kept silent fades.',
   'A thank-you spoken out loud does something that a thank-you merely thought does not.',
   'Express gratitude to someone today, specifically and directly.'],
  ['The people around you are not characters in your story. They are the authors of theirs.',
   'Recognizing that every person has a narration as complex as yours changes how you relate to them.',
   'Consider a recent interaction from the other person\'s perspective, as the protagonist of their own story.'],
  ['Presence is the rarest gift you can offer another person.',
   'In a world of divided attention, being fully present with someone is an act of respect.',
   'Give someone your full, undivided attention for at least ten minutes today.'],

  // ── recuperation (45-53) ──
  ['Rest is not the opposite of effort; it is the condition for its renewal.',
   'A muscle grows during rest, not during the effort that stimulates it.',
   'Identify a moment of rest today and treat it as an essential part of your performance, not a concession.'],
  ['Fatigue that is denied accumulates. Fatigue that is acknowledged can be addressed.',
   'Pretending not to be tired does not make the fatigue disappear; it only postpones its expression.',
   'Assess your current level of fatigue honestly, without judgment.'],
  ['A pause taken freely restores more than a pause imposed by exhaustion.',
   'Choosing to stop before breaking down preserves dignity and the ability to choose when to resume.',
   'Take a deliberate pause before you feel you absolutely need one.'],
  ['Sleep is not time lost. It is the maintenance the mind and body need.',
   'No machine runs without downtime; the human body and mind are no exception.',
   'Protect your next night\'s sleep as you would protect an important appointment.'],
  ['Recovery is active, not passive. It requires intention, not just absence of effort.',
   'Lying down exhausted is not the same as deliberately restoring your resources.',
   'Choose one recovery practice today and do it with intention.'],
  ['The body speaks before the mind admits. Listen to the first signal.',
   'Physical symptoms often precede the conscious recognition of exhaustion or stress.',
   'Notice one physical signal your body is sending you right now.'],
  ['What recharges you is personal. Do not borrow someone else\'s recovery recipe without testing it.',
   'What restores one person may drain another; recovery requires self-knowledge.',
   'Identify what genuinely restores you, as opposed to what you think should restore you.'],
  ['A day of true rest is not a day wasted. It is an investment in the days to come.',
   'The productivity lost during a rest day is recovered many times over in the quality of the days that follow.',
   'If you feel guilty about resting, ask yourself what that guilt is protecting.'],
  ['Recovery is not a reward you must earn. It is a right you must exercise.',
   'Waiting until you have "done enough" to rest often means waiting until you collapse.',
   'Rest before you feel you deserve it. Notice what changes.'],

  // ── travail (54-62) ──
  ['Deep work requires deep attention. Protect it like a scarce resource.',
   'The quality of what you produce depends directly on the quality of the attention you bring to it.',
   'Identify one block of time today that you will protect from all interruptions.'],
  ['Multiplying tasks dilutes the quality of each. Doing less, better, is a strategic choice.',
   'Three tasks completed well advance you more than ten started and abandoned.',
   'Choose three things today and do them to completion before starting anything else.'],
  ['The most important task is rarely the most urgent one.',
   'Urgency is often a signal of someone else\'s priority; importance is a signal of your own direction.',
   'Before reacting to an urgent demand, ask whether it serves what matters most to you.'],
  ['A completed modest task advances you. A perfect unfinished project does not.',
   'Done beats perfect when perfect never ships.',
   'Finish one thing today that is good enough rather than perfect.'],
  ['Your work does not define your worth, but how you work says something about who you are.',
   'The value of your work and your value as a person are distinct; your approach to work reveals your character.',
   'Notice how you worked today without judging the result. What does your approach say about you?'],
  ['The environment shapes the work. Arrange your space with intention.',
   'A cluttered environment produces a scattered mind; an intentional space supports intentional work.',
   'Remove one unnecessary thing from your workspace today.'],
  ['Alternating effort and genuine breaks produces more than continuous straining.',
   'The mind needs oscillation between focus and diffusion to produce its best work.',
   'After your next focused session, take a real break — no screen, just stillness or movement.'],
  ['Comparing your output to someone else\'s is comparing your beginning to their middle.',
   'You see the result of another person\'s accumulated work, not the years of invisible effort.',
   'Catch yourself comparing your productivity to someone else\'s and remember what you do not see.'],
  ['Clarity of objective is the first tool of effective work.',
   'Knowing exactly what "done" looks like before you start prevents drifting and rework.',
   'Before starting your next task, define in one sentence what completing it means.'],

  // ── crainte (63-71) ──
  ['Fear is a signal, not a decision-maker.',
   'Fear informs you of a risk; it does not get to decide what you do about it.',
   'Identify a fear that has been making your decisions. Separate the information from the choice.'],
  ['What you fear is rarely what actually happens. The anticipation is the main suffering.',
   'The mind\'s simulation of a feared event is almost always worse than the event itself, if it ever occurs.',
   'Notice a fear you have been anticipating and ask how much of the suffering is happening right now.'],
  ['Courage is not the absence of fear but the decision that something matters more than the fear.',
   'Feeling fear and acting anyway is not the same as not feeling it.',
   'Identify something that matters enough to you to act despite the fear it triggers.'],
  ['The fear of judgment shrinks your life more than judgment itself ever could.',
   'The anticipation of what others might think stops more actions than actual criticism ever does.',
   'Do one thing today that you would do if no one were watching or judging.'],
  ['Naming a fear reduces its hold. What remains unnamed stays in the shadows and grows.',
   'Putting a fear into words brings it into the light where its real size becomes visible.',
   'Write down a fear you have been carrying. Describe it precisely.'],
  ['Most fears are not about the present moment. They are stories about a future that does not exist.',
   'Fear lives in the gap between now and a possible later. The present moment rarely contains the threat.',
   'When fear arises, ask: is this happening now, or am I imagining a future?'],
  ['The fear of failing often causes the failure it tries to prevent.',
   'The energy spent avoiding failure is sometimes the very energy that would have produced success.',
   'Identify an area where your fear of failing is shaping your behavior more than your desire to succeed.'],
  ['You have survived every fear you have ever faced so far.',
   'Your track record against fear is perfect — not because you never felt it, but because you are still here.',
   'Remember a past fear that once seemed overwhelming. Where is it now?'],
  ['Fear narrows the field of vision. Action, even small, widens it again.',
   'A single step taken despite fear reveals options that were invisible from the frozen position.',
   'Take one small action in a domain where fear has been keeping you still.'],

  // ── temps (72-80) ──
  ['Time spent is never recovered, but it is never entirely lost if you were present for it.',
   'A moment fully lived is not wasted, regardless of its productive outcome.',
   'Be fully present for one ordinary moment today, without trying to optimize it.'],
  ['The feeling of lacking time often masks a difficulty in choosing what to leave aside.',
   '"I don\'t have time" often means "I haven\'t decided what matters less."',
   'Name something you will deliberately not do today to make space for what matters.'],
  ['Your attention is the only currency you truly spend. Spend it deliberately.',
   'Where your attention goes, your life goes. Everything else is just logistics.',
   'Notice where your attention went today. Was it spent on what matters to you?'],
  ['Waiting for the right moment is often a disguised form of avoidance.',
   'The right moment is rarely announced in advance; it becomes right through the decision to act.',
   'Start something today that you have been waiting for the right moment to begin.'],
  ['The past is a teacher, not a residence.',
   'Visiting the past to learn is useful; living there is not.',
   'Identify a past lesson you have been using as a reason not to move forward.'],
  ['Patience is not passive waiting. It is active confidence in the process.',
   'Waiting without anxiety is a skill; it requires trust in what has been set in motion.',
   'Identify something you are impatient about and ask what trusting the process would look like.'],
  ['A deadline is a tool, not a threat. Use it to focus, not to panic.',
   'A time constraint can clarify priorities if you let it, rather than letting it overwhelm you.',
   'If a deadline is stressing you, ask what essential thing it asks you to protect.'],
  ['The speed of the world does not have to be your speed.',
   'You can choose a different rhythm than the one imposed by your environment.',
   'Deliberately slow down one activity today that you usually rush through.'],
  ['What you do with a day is what you do with a life, one unit at a time.',
   'A life is not built in grand moments but in the accumulation of ordinary days well lived.',
   'Live today as if it were a representative sample of the life you are building.'],

  // ── clarte (81-89) ──
  ['Clarity does not come from adding information. It comes from removing what is not essential.',
   'The path becomes visible not when you gather more but when you set aside what obscures it.',
   'Remove one unnecessary thing from your life today — a commitment, an object, a thought pattern.'],
  ['A clear question already contains half the answer.',
   'The effort of formulating a precise question often reveals the answer that was hidden behind a vague worry.',
   'Turn a vague concern into a precise question. Ask it directly.'],
  ['What you cannot explain simply, you do not yet understand fully.',
   'The ability to restate something in plain terms is the test of genuine comprehension.',
   'Take a concept you use often and try to explain it in one sentence.'],
  ['The fog clears through action, not through more thinking.',
   'You cannot think your way to clarity about something you have never attempted.',
   'Stop analyzing something and take one concrete step. Observe what becomes clear.'],
  ['Clarity of purpose simplifies every decision that follows.',
   'When you know why you are doing something, what to do and what to ignore become obvious.',
   'State the purpose of your current main project in one sentence.'],
  ['Confusion is often the sign of a question poorly framed, not of a mind incapable of understanding.',
   'Reformulating the question is sometimes all that is needed to see the answer.',
   'Take a problem that confuses you and reframe it from a different angle.'],
  ['Simplicity is not the starting point. It is the destination of serious thought.',
   'Arriving at simplicity requires going through complexity, not avoiding it.',
   'Take a complex idea or situation and distill it to its simplest truthful form.'],
  ['A mind cluttered with open loops cannot think clearly.',
   'Every unfinished task, unresolved decision, or unprocessed emotion occupies mental space.',
   'Close one open loop today: decide, complete, or deliberately postpone it.'],
  ['Clarity is a practice, not a permanent state.',
   'You do not reach clarity once and stay there; you return to it, again and again, through the same disciplines.',
   'Identify a practice that brings you clarity and commit to returning to it this week.'],
];

const PHILOSOPHY_ES: readonly EntryI18n[] = [
  // ── acción (0-8) ──
  ['Lo que depende de ti merece tu esfuerzo; lo que no depende merece tu tranquilidad.',
   'Separar ambos evita gastar energía en lo que no puedes cambiar.',
   'Nombra una cosa de tu día que realmente dependa de ti.'],
  ['Actuar con justicia cuenta más que tener éxito rápido.',
   'La rectitud de una acción sigue siendo verdadera incluso cuando su resultado tarda.',
   'Elige hoy una acción por su rectitud más que por su rapidez.'],
  ['Una acción comenzada vale más que un proyecto perfecto que permanece inmóvil.',
   'Comenzar, aunque sea imperfectamente, ya produce información que la sola planificación no da.',
   'Empieza la versión más simple de algo que has estado posponiendo.'],
  ['El control que tienes sobre tu actitud permanece completo, incluso cuando todo lo demás escapa.',
   'La actitud es uno de los pocos terrenos donde el control jamás depende de las circunstancias.',
   'Elige conscientemente tu actitud para la próxima hora.'],
  ['Hacer lo que está en tu poder es suficiente; el resto no te pertenece.',
   'Exigirte lo que supera tu poder real produce fatiga sin beneficio.',
   'Identifica una exigencia que pones sobre un resultado que no depende enteramente de ti.'],
  ['Cada gesto hecho hoy construye la capacidad de mañana.',
   'Una capacidad se construye por repetición, rara vez por un solo gesto aislado.',
   'Encuentra un gesto modesto de hoy que sirva a un objetivo más amplio.'],
  ['La intención sola no cambia nada; el gesto, aunque modesto, cambia algo.',
   'Una intención no traducida en gesto permanece sin efecto sobre la realidad.',
   'Convierte una intención actual en un gesto concreto, por pequeño que sea.'],
  ['Elige la acción que sigue siendo justa, independientemente de su resultado.',
   'El resultado depende de múltiples factores; la rectitud de la acción permanece bajo tu control.',
   'Reconsidera una decisión reciente solo a la luz de su rectitud, no de su desenlace.'],
  ['Lo que haces con constancia pesa más que lo que haces con intensidad.',
   'Un esfuerzo puntual, aunque intenso, rara vez produce lo que produce una constancia modesta.',
   'Elige una acción que puedas repetir mañana, incluso a intensidad reducida.'],

  // ── percepción (9-17) ──
  ['Describir una situación sin exagerarla es ya un acto de lucidez.',
   'Quitar la capa dramática de una descripción deja que los hechos reales emerjan.',
   'Describe una dificultad actual usando solo hechos observables, sin adjetivos.'],
  ['Tu juicio sobre un evento no es el evento mismo.',
   'Lo que añades a un hecho mediante la interpretación suele pesar más que el hecho mismo.',
   'Identifica un juicio que has añadido hoy a un evento neutro.'],
  ['Lo que ves depende de dónde estás parado. Muévete antes de concluir.',
   'Cambiar de perspectiva revela a menudo dimensiones invisibles desde la posición original.',
   'Observa una situación actual desde la perspectiva de otra persona durante un momento.'],
  ['El mismo hecho puede ser una catástrofe o una señal, según la historia que te cuentes.',
   'La narrativa que envuelve un hecho determina la respuesta emocional más que el hecho mismo.',
   'Toma un evento que te molestó y cuéntalo de otra manera, como lo haría un observador neutro.'],
  ['Antes de interpretar, observa. Antes de juzgar, describe.',
   'Apresurarse a interpretar cierra a menudo la puerta a lo que la observación aún podría revelar.',
   'Pasa dos minutos observando algo sin etiquetarlo como bueno o malo.'],
  ['Lo que te molesta en los demás refleja a menudo algo que te niegas a ver en ti mismo.',
   'La irritación provocada por otra persona señala a veces una parte no reconocida de uno mismo.',
   'Nota una cosa que te irritó hoy y pregúntate qué podría decir sobre ti.'],
  ['Una perspectiva estrecha reduce el campo de posibilidades.',
   'Cuando una sola interpretación domina, otras lecturas plausibles se vuelven invisibles.',
   'Identifica una situación donde solo tienes una interpretación e imagina otras dos.'],
  ['No ves el mundo como es; lo ves como eres.',
   'Toda percepción está filtrada por tu estado actual, no por un lente neutro.',
   'Nota cómo tu estado de ánimo actual colorea lo que percibes en este momento.'],
  ['La calidad de tu atención moldea la calidad de tu juicio.',
   'Una atención dispersa produce juicios aproximados; una atención enfocada los refina.',
   'Dedica tu atención plena a una sola cosa durante los próximos cinco minutos.'],

  // ── aceptación (18-26) ──
  ['Aceptar no significa aprobar. Significa dejar de luchar contra lo que ya es.',
   'La energía gastada combatiendo un hecho establecido es energía indisponible para lo que aún puede cambiar.',
   'Identifica algo contra lo que estás luchando que ya pasó o es inmutable.'],
  ['Lo que te resiste no es necesariamente tu enemigo.',
   'Un obstáculo a veces te protege de un camino que no era el correcto, o te enseña lo que el camino correcto exige.',
   'Encuentra una cosa que te resistió esta semana y considera qué pudo haberte enseñado.'],
  ['El momento presente nunca es insoportable cuando dejas de añadirle el ayer y el mañana.',
   'Lo que vuelve pesado el presente es a menudo la acumulación del arrepentimiento pasado y la ansiedad futura.',
   'Concéntrate durante un minuto en lo que sucede ahora mismo, sin añadir lo anterior ni lo siguiente.'],
  ['No puedes cambiar lo que ya ocurrió, solo lo que haces con ello.',
   'El pasado está fijado; tu relación con él permanece abierta.',
   'Elige un evento pasado y decide qué harás con él hoy, en lugar de lo que él te hizo.'],
  ['Resistir lo que ya es verdad consume energía que la acción podría usar.',
   'Aceptar una realidad libera la energía que la negación usaba para mantenerla a raya.',
   'Nombra una realidad que has estado resistiendo y siente lo que cambia cuando simplemente la nombras.'],
  ['Lo que aceptas deja de ocupar tu mente; lo que resistes se queda.',
   'El espacio mental ocupado por la resistencia no está disponible para la creación o la decisión.',
   'Identifica un pensamiento que vuelve constantemente y acepta su presencia sin luchar contra él.'],
  ['Algunas cosas no te corresponden llevarlas.',
   'Distinguir entre lo que te pertenece y lo que pertenece al mundo aligera la carga.',
   'Suelta una preocupación que realmente no te corresponde llevar.'],
  ['Aceptar tus límites no es debilidad; es la condición para superarlos.',
   'Saber dónde están tus límites te permite trabajar con ellos en lugar de contra ellos.',
   'Nombra un límite que has estado negando y considera cómo reconocerlo podría servirte.'],
  ['Lo que es, es. Tu poder reside en lo que haces a continuación.',
   'Ninguna protesta cambia un hecho establecido; solo importa la próxima acción.',
   'Enfrenta un hecho que has estado evitando y pregúntate solo: ¿qué hago ahora?'],

  // ── disciplina (27-35) ──
  ['La disciplina no es castigo. Es la estructura que protege lo que te importa.',
   'Un marco libremente elegido hace posible la libertad; sin él, cada día empieza desde cero.',
   'Identifica una pequeña disciplina que ya protege algo importante para ti.'],
  ['La dificultad de una disciplina disminuye con su regularidad.',
   'Lo que cuesta un esfuerzo enorme la primera vez se vuelve casi automático tras suficientes repeticiones.',
   'Elige una disciplina que quieras fortalecer y comprométete a una versión mínima cada día esta semana.'],
  ['Lo que haces cada día cuenta más que lo que haces de vez en cuando.',
   'La acumulación de pequeñas acciones diarias supera el gesto grandioso ocasional.',
   'Encuentra una pequeña cosa que haces cada día que, acumulativamente, ya ha cambiado algo.'],
  ['La disciplina es elegir lo que más deseas sobre lo que deseas ahora.',
   'Cada elección disciplinada es un voto por un deseo de largo plazo contra un impulso inmediato.',
   'Identifica una elección hoy donde puedas favorecer lo que deseas profundamente sobre lo que deseas ya.'],
  ['Una disciplina impuesta por el miedo no dura. Una disciplina elegida por su significado perdura.',
   'La presión externa produce obediencia; el significado interno produce compromiso.',
   'Examina una de tus disciplinas: ¿viene del miedo o del significado?'],
  ['La forma de una disciplina puede cambiar; su presencia en tu vida no puede.',
   'Una rutina interrumpida no significa una disciplina perdida — solo una que necesita una nueva forma.',
   'Encuentra una disciplina que ha sido interrumpida y dale una forma que se ajuste a tu vida actual.'],
  ['La disciplina no es rigidez. Es la columna vertebral que permite que el resto sea flexible.',
   'Un marco sólido permite la improvisación sin desmoronarse.',
   'Nota dónde una estructura sólida en tu vida te permite ser flexible en otras áreas.'],
  ['Fallar una vez es un momento. Fallar dos veces es el comienzo de un nuevo patrón.',
   'Un solo desliz es ruido; dos seguidos son una señal que merece atención.',
   'Observa un desliz reciente y decide si fue un momento o el inicio de un patrón.'],
  ['La verdadera libertad no es la ausencia de restricciones, sino el dominio de cuáles aceptas.',
   'Elegir tus restricciones deliberadamente es lo opuesto a estar aprisionado por ellas.',
   'Nombra una restricción que has elegido libremente y que de hecho aumenta tu libertad.'],

  // ── relaciones (36-44) ──
  ['La calidad de tu escucha determina la calidad de tu presencia.',
   'Escuchar no es esperar tu turno para hablar; es hacer espacio para que la otra persona exista.',
   'En tu próxima conversación, escucha sin preparar tu respuesta.'],
  ['No puedes controlar lo que los demás piensen de ti, solo la integridad con la que actúas.',
   'La energía gastada en gestionar tu imagen es energía robada a ser quien eres.',
   'Identifica un momento hoy en que actuaste para manejar una impresión en lugar de desde la integridad.'],
  ['Una palabra sincera, aunque torpe, pesa más que un silencio pulido.',
   'Decir algo imperfecto pero verdadero fortalece un vínculo más que no decir nada perfectamente.',
   'Dile algo verdadero a alguien hoy, aunque las palabras no sean perfectas.'],
  ['Esperar que los demás lean tu mente es darles una tarea en la que siempre fracasarán.',
   'Las expectativas no expresadas son obligaciones invisibles que la otra persona nunca aceptó.',
   'Nombra una expectativa que tienes hacia alguien que nunca has expresado claramente.'],
  ['Un conflicto bien manejado acerca a las personas. Un conflicto evitado las aleja.',
   'Evitar una conversación difícil no preserva la paz; preserva la distancia.',
   'Identifica una conversación que has estado evitando y considera qué podría abrir si se maneja bien.'],
  ['No eres responsable de las emociones de los demás, solo de tu propia conducta.',
   'Intentar gestionar el estado emocional de otro adulto es imposible y agotador.',
   'Nota dónde estás cargando con la responsabilidad del estado emocional de otra persona.'],
  ['La gratitud expresada fortalece el vínculo. La gratitud callada se desvanece.',
   'Un gracias dicho en voz alta hace algo que un gracias solo pensado no logra.',
   'Expresa gratitud a alguien hoy, de manera específica y directa.'],
  ['Las personas a tu alrededor no son personajes de tu historia. Son los autores de la suya.',
   'Reconocer que cada persona tiene una narración tan compleja como la tuya cambia cómo te relacionas con ella.',
   'Considera una interacción reciente desde la perspectiva de la otra persona, como protagonista de su propia historia.'],
  ['La presencia es el regalo más valioso que puedes ofrecer a otra persona.',
   'En un mundo de atención dividida, estar plenamente presente con alguien es un acto de respeto.',
   'Regálale a alguien tu atención plena durante al menos diez minutos hoy.'],

  // ── recuperación (45-53) ──
  ['El descanso no es lo opuesto al esfuerzo; es la condición para su renovación.',
   'Un músculo crece durante el descanso, no durante el esfuerzo que lo estimula.',
   'Identifica un momento de descanso hoy y trátalo como parte esencial de tu rendimiento, no como una concesión.'],
  ['La fatiga negada se acumula. La fatiga reconocida puede ser atendida.',
   'Fingir que no estás cansado no hace desaparecer la fatiga; solo pospone su expresión.',
   'Evalúa tu nivel actual de fatiga con honestidad, sin juicio.'],
  ['Una pausa tomada libremente restaura más que una pausa impuesta por el agotamiento.',
   'Elegir detenerse antes de romperse preserva la dignidad y la capacidad de elegir cuándo retomar.',
   'Toma una pausa deliberada antes de sentir que la necesitas absolutamente.'],
  ['Dormir no es tiempo perdido. Es el mantenimiento que la mente y el cuerpo necesitan.',
   'Ninguna máquina funciona sin tiempo de inactividad; el cuerpo y la mente humanos no son la excepción.',
   'Protege tu próxima noche de sueño como protegerías una cita importante.'],
  ['La recuperación es activa, no pasiva. Requiere intención, no solo ausencia de esfuerzo.',
   'Tumbarse agotado no es lo mismo que restaurar deliberadamente tus recursos.',
   'Elige una práctica de recuperación hoy y hazla con intención.'],
  ['El cuerpo habla antes de que la mente lo admita. Escucha la primera señal.',
   'Los síntomas físicos a menudo preceden al reconocimiento consciente del agotamiento o el estrés.',
   'Nota una señal física que tu cuerpo te está enviando ahora mismo.'],
  ['Lo que te recarga es personal. No tomes prestada la receta de recuperación de otro sin probarla.',
   'Lo que restaura a una persona puede agotar a otra; la recuperación requiere autoconocimiento.',
   'Identifica lo que genuinamente te restaura, en contraste con lo que crees que debería restaurarte.'],
  ['Un día de verdadero descanso no es un día perdido. Es una inversión en los días por venir.',
   'La productividad perdida durante un día de descanso se recupera con creces en la calidad de los días siguientes.',
   'Si sientes culpa por descansar, pregúntate qué está protegiendo esa culpa.'],
  ['La recuperación no es una recompensa que debas ganar. Es un derecho que debes ejercer.',
   'Esperar hasta haber "hecho suficiente" para descansar significa a menudo esperar hasta desplomarse.',
   'Descansa antes de sentir que lo mereces. Nota lo que cambia.'],

  // ── trabajo (54-62) ──
  ['El trabajo profundo requiere atención profunda. Protégela como un recurso escaso.',
   'La calidad de lo que produces depende directamente de la calidad de la atención que le aportas.',
   'Identifica un bloque de tiempo hoy que protegerás de toda interrupción.'],
  ['Multiplicar tareas diluye la calidad de cada una. Hacer menos, mejor, es una elección estratégica.',
   'Tres tareas bien terminadas te hacen avanzar más que diez empezadas y abandonadas.',
   'Elige tres cosas hoy y llévalas a término antes de empezar cualquier otra.'],
  ['La tarea más importante rara vez es la más urgente.',
   'La urgencia es a menudo la señal de la prioridad de otro; la importancia es la señal de tu propia dirección.',
   'Antes de reaccionar a una demanda urgente, pregúntate si sirve a lo que más te importa.'],
  ['Una tarea modesta terminada te hace avanzar. Un proyecto perfecto e inacabado no.',
   'Hecho vence a perfecto cuando lo perfecto nunca se entrega.',
   'Termina una cosa hoy que esté suficientemente bien en lugar de perfecta.'],
  ['Tu trabajo no define tu valor, pero cómo trabajas dice algo sobre quién eres.',
   'El valor de tu trabajo y tu valor como persona son distintos; tu forma de trabajar revela tu carácter.',
   'Observa cómo trabajaste hoy sin juzgar el resultado. ¿Qué dice tu enfoque sobre ti?'],
  ['El entorno moldea el trabajo. Ordena tu espacio con intención.',
   'Un entorno desordenado produce una mente dispersa; un espacio intencional apoya el trabajo intencional.',
   'Quita una cosa innecesaria de tu espacio de trabajo hoy.'],
  ['Alternar esfuerzo y pausas verdaderas produce más que el esfuerzo continuo.',
   'La mente necesita oscilación entre enfoque y difusión para producir su mejor trabajo.',
   'Después de tu próxima sesión de concentración, toma una pausa real — sin pantalla, solo quietud o movimiento.'],
  ['Comparar tu producción con la de otro es comparar tu comienzo con su mitad.',
   'Ves el resultado del trabajo acumulado de otra persona, no los años de esfuerzo invisible.',
   'Sorpréndete comparando tu productividad con la de otro y recuerda lo que no ves.'],
  ['La claridad del objetivo es la primera herramienta del trabajo eficaz.',
   'Saber exactamente qué significa "terminado" antes de empezar evita la deriva y el retrabajo.',
   'Antes de comenzar tu próxima tarea, define en una frase lo que significa completarla.'],

  // ── temor (63-71) ──
  ['El miedo es una señal, no un tomador de decisiones.',
   'El miedo te informa de un riesgo; no le corresponde decidir qué haces al respecto.',
   'Identifica un miedo que ha estado tomando tus decisiones. Separa la información de la elección.'],
  ['Lo que temes rara vez es lo que realmente sucede. La anticipación es el sufrimiento principal.',
   'La simulación mental de un evento temido es casi siempre peor que el evento mismo, si es que ocurre.',
   'Nota un miedo que has estado anticipando y pregúntate cuánto del sufrimiento está ocurriendo ahora.'],
  ['El coraje no es la ausencia de miedo, sino la decisión de que algo importa más que el miedo.',
   'Sentir miedo y actuar de todos modos no es lo mismo que no sentirlo.',
   'Identifica algo que te importe lo suficiente como para actuar a pesar del miedo que provoca.'],
  ['El miedo al juicio reduce tu vida más de lo que el juicio mismo jamás podría.',
   'La anticipación de lo que otros podrían pensar detiene más acciones que las críticas reales.',
   'Haz una cosa hoy que harías si nadie estuviera mirando o juzgando.'],
  ['Nombrar un miedo reduce su dominio. Lo que queda sin nombre permanece en las sombras y crece.',
   'Poner un miedo en palabras lo trae a la luz, donde su tamaño real se vuelve visible.',
   'Escribe un miedo que has estado cargando. Descríbelo con precisión.'],
  ['La mayoría de los miedos no tratan del momento presente. Son historias sobre un futuro que no existe.',
   'El miedo vive en la brecha entre el ahora y un posible después. El momento presente rara vez contiene la amenaza.',
   'Cuando surja el miedo, pregúntate: ¿está ocurriendo esto ahora o estoy imaginando un futuro?'],
  ['El miedo a fracasar a menudo causa el fracaso que intenta prevenir.',
   'La energía gastada en evitar el fracaso es a veces la misma energía que habría producido el éxito.',
   'Identifica un área donde tu miedo a fracasar moldea tu comportamiento más que tu deseo de triunfar.'],
  ['Has sobrevivido a cada miedo que has enfrentado hasta ahora.',
   'Tu historial contra el miedo es perfecto — no porque nunca lo hayas sentido, sino porque sigues aquí.',
   'Recuerda un miedo pasado que alguna vez pareció abrumador. ¿Dónde está ahora?'],
  ['El miedo estrecha el campo de visión. La acción, incluso pequeña, lo ensancha de nuevo.',
   'Un solo paso dado a pesar del miedo revela opciones que eran invisibles desde la posición congelada.',
   'Da un pequeño paso en un ámbito donde el miedo te ha mantenido quieto.'],

  // ── tiempo (72-80) ──
  ['El tiempo gastado nunca se recupera, pero nunca se pierde del todo si estuviste presente.',
   'Un momento plenamente vivido no es desperdiciado, independientemente de su resultado productivo.',
   'Está plenamente presente en un momento ordinario hoy, sin intentar optimizarlo.'],
  ['La sensación de falta de tiempo a menudo esconde una dificultad para elegir qué dejar de lado.',
   '"No tengo tiempo" significa a menudo "no he decidido qué importa menos".',
   'Nombra algo que deliberadamente no harás hoy para hacer espacio a lo que importa.'],
  ['Tu atención es la única moneda que realmente gastas. Gástala deliberadamente.',
   'Donde va tu atención, va tu vida. Todo lo demás es logística.',
   'Nota dónde fue tu atención hoy. ¿Se gastó en lo que te importa?'],
  ['Esperar el momento adecuado es a menudo una forma disfrazada de evasión.',
   'El momento adecuado rara vez se anuncia por adelantado; se vuelve adecuado mediante la decisión de actuar.',
   'Empieza algo hoy que has estado esperando el momento adecuado para comenzar.'],
  ['El pasado es un maestro, no una residencia.',
   'Visitar el pasado para aprender es útil; vivir allí no lo es.',
   'Identifica una lección del pasado que has estado usando como razón para no avanzar.'],
  ['La paciencia no es espera pasiva. Es confianza activa en el proceso.',
   'Esperar sin ansiedad es una habilidad; requiere confianza en lo que se ha puesto en marcha.',
   'Identifica algo que te impacienta y pregúntate cómo sería confiar en el proceso.'],
  ['Una fecha límite es una herramienta, no una amenaza. Úsala para enfocarte, no para entrar en pánico.',
   'Una restricción de tiempo puede clarificar prioridades si lo permites, en lugar de dejar que te abrume.',
   'Si una fecha límite te estresa, pregúntate qué es lo esencial que te pide proteger.'],
  ['La velocidad del mundo no tiene por qué ser tu velocidad.',
   'Puedes elegir un ritmo diferente al que impone tu entorno.',
   'Reduce deliberadamente la velocidad de una actividad hoy que normalmente haces con prisa.'],
  ['Lo que haces con un día es lo que haces con una vida, una unidad a la vez.',
   'Una vida no se construye en grandes momentos, sino en la acumulación de días ordinarios bien vividos.',
   'Vive hoy como si fuera una muestra representativa de la vida que estás construyendo.'],

  // ── claridad (81-89) ──
  ['La claridad no viene de añadir información. Viene de quitar lo que no es esencial.',
   'El camino se vuelve visible no cuando acumulas más, sino cuando apartas lo que lo oscurece.',
   'Quita una cosa innecesaria de tu vida hoy — un compromiso, un objeto, un patrón de pensamiento.'],
  ['Una pregunta clara ya contiene la mitad de la respuesta.',
   'El esfuerzo de formular una pregunta precisa revela a menudo la respuesta que se ocultaba tras una preocupación vaga.',
   'Convierte una preocupación vaga en una pregunta precisa. Hazla directamente.'],
  ['Lo que no puedes explicar con sencillez, aún no lo entiendes completamente.',
   'La capacidad de reformular algo en términos simples es la prueba de la comprensión genuina.',
   'Toma un concepto que usas a menudo e intenta explicarlo en una sola frase.'],
  ['La niebla se disipa mediante la acción, no mediante más pensamiento.',
   'No puedes pensar tu camino hacia la claridad sobre algo que nunca has intentado.',
   'Deja de analizar algo y da un paso concreto. Observa lo que se vuelve claro.'],
  ['La claridad de propósito simplifica cada decisión que sigue.',
   'Cuando sabes por qué haces algo, qué hacer y qué ignorar se vuelven obvios.',
   'Enuncia el propósito de tu proyecto principal actual en una sola frase.'],
  ['La confusión es a menudo la señal de una pregunta mal formulada, no de una mente incapaz de comprender.',
   'Reformular la pregunta es a veces todo lo que se necesita para ver la respuesta.',
   'Toma un problema que te confunde y reformúlalo desde un ángulo diferente.'],
  ['La simplicidad no es el punto de partida. Es el destino del pensamiento serio.',
   'Llegar a la simplicidad requiere atravesar la complejidad, no evitarla.',
   'Toma una idea o situación compleja y destílala a su forma más simple y verdadera.'],
  ['Una mente abarrotada de asuntos pendientes no puede pensar con claridad.',
   'Cada tarea sin terminar, decisión sin resolver o emoción no procesada ocupa espacio mental.',
   'Cierra un asunto pendiente hoy: decide, completa o pospón deliberadamente.'],
  ['La claridad es una práctica, no un estado permanente.',
   'No alcanzas la claridad una vez y te quedas allí; vuelves a ella, una y otra vez, mediante las mismas disciplinas.',
   'Identifica una práctica que te aporta claridad y comprométete a retomarla esta semana.'],
];

export function getPhilosophyTranslation(
  index: number,
  locale: string,
): EntryI18n | undefined {
  if (locale === 'en-US') return PHILOSOPHY_EN[index];
  if (locale === 'es-MX') return PHILOSOPHY_ES[index];
  return undefined;
}
